import React, { ReactNode, useEffect } from 'react';
import { useDemoRatio } from '@/contexts/DemoContext';
import { applyRatioStyles } from './DemoRatioToggle';

interface DemoRatioApplierProps {
  children: ReactNode;
}

/**
 * Applies portrait ratio styles to the body/html elements
 * This ensures the ratio effect works on all pages, not just those wrapped in DemoWrapper
 */
export default function DemoRatioApplier({ children }: DemoRatioApplierProps) {
  const { portraitRatio } = useDemoRatio();

  useEffect(() => {
    const styles = applyRatioStyles(portraitRatio);
    const body = document.body;
    const html = document.documentElement;

    // Apply styles to body - center the container on viewport
    if (portraitRatio === 'freestyle') {
      body.style.maxWidth = 'none';
      body.style.width = '100%';
      body.style.margin = '0';
      body.style.aspectRatio = 'unset';
      body.style.display = 'block';
      body.style.position = 'relative';
    } else {
      // Center the container horizontally on the viewport
      body.style.maxWidth = styles.maxWidth || 'none';
      body.style.aspectRatio = styles.aspectRatio || 'unset';
      body.style.width = styles.maxWidth || '100%';
      body.style.margin = '0 auto';
      body.style.display = 'block';
      body.style.position = 'relative';
      body.style.minHeight = '100vh';
    }

    // Apply to html - ensure full height for centering
    if (portraitRatio === 'freestyle') {
      html.style.maxWidth = 'none';
      html.style.width = '100%';
      html.style.height = 'auto';
      html.style.margin = '0';
    } else {
      html.style.maxWidth = '100%';
      html.style.width = '100%';
      html.style.height = '100%';
      html.style.margin = '0';
      html.style.display = 'block';
    }

    // Cleanup function
    return () => {
      body.style.maxWidth = '';
      body.style.width = '';
      body.style.margin = '';
      body.style.aspectRatio = '';
      body.style.display = '';
      body.style.position = '';
      body.style.minHeight = '';
      html.style.maxWidth = '';
      html.style.width = '';
      html.style.height = '';
      html.style.margin = '';
      html.style.display = '';
    };
  }, [portraitRatio]);

  return <>{children}</>;
}

